import {createAsyncThunk} from '@reduxjs/toolkit';
import type {RootState} from '@/store';
import type {EventHashedID} from '@Components/events/events.types';
import {getActiveOrdersList, getTicketingErrorCodeFromAjaxError} from '@Libraries/event-ticketing.library';
import {LoadingStates} from '@Utils/loading.util';
import type {
  AddTicketingAttendeeArgs,
  CancelTicketingAttendeesArgs,
  CheckInTicketingAttendeeArgs,
  CheckInTicketingOrderArgs,
  FetchTicketingOrderSummariesArgs,
  FetchTicketingOrdersArgs,
  MarkTicketOrderAsPaidArgs,
  RefundTicketOrderArgs,
  TicketingDashboardFetchError,
  TicketingDashboardResponse,
  TicketingOrderSummary,
  TicketingOrdersBatch,
  TicketingRefundResult,
  TicketingRsvpStats,
  TicketingStats,
} from '@Components/event-ticketing/event-ticketing.types';

/**
 * Single round-trip for the dashboard's initial load: the backend resolves + authorizes the event, reads its
 * stats, and returns the first page of orders in one request. Not cached — each visit re-fetches and the reducer
 * clears the prior event's data.
 */
export const fetchTicketingDashboard = createAsyncThunk<TicketingDashboardResponse, EventHashedID, {rejectValue: TicketingDashboardFetchError}>(
  'eventTicketing/fetchDashboard',
  async (eventHashedID: EventHashedID, {rejectWithValue}) => {
    try {
      return (await window.PMW.readLocal('eventticketing/getTicketingDashboard', {event_hid: eventHashedID})) as TicketingDashboardResponse;
    } catch (error) {
      // The code separates "paid ticketing is off for you" — which redirects out — from a transient failure,
      // which keeps the retry message. Without it every failure would look the same to the reducer.
      return rejectWithValue({code: getTicketingErrorCodeFromAjaxError(error)});
    }
  }
);

/**
 * Re-reads just the paid stats row (B1) after an action that moves the numbers (refund, mark-as-paid). The
 * fulfilled reducer swaps `stats` in place with no loading state, so the row updates without flashing skeletons.
 */
export const fetchTicketingStats = createAsyncThunk<TicketingStats, EventHashedID>('eventTicketing/fetchStats', (eventHashedID: EventHashedID) => {
  return window.PMW.readLocal('eventticketing/getTicketingStats', {event_hid: eventHashedID});
});

/**
 * The RSVP counterpart of fetchTicketingStats: re-reads the RSVP stats row (registrations / page views /
 * cancellations) after an add-attendee or cancel. Swaps `rsvpStats` in place with no loading state.
 */
export const fetchRsvpTicketingStats = createAsyncThunk<TicketingRsvpStats, EventHashedID>('eventTicketing/fetchRsvpStats', (eventHashedID: EventHashedID) => {
  return window.PMW.readLocal('eventticketing/getRsvpTicketingStats', {event_hid: eventHashedID});
});

/**
 * Re-reads the free ($0) event stats row (tickets sold / page views / cancellations) after an add-attendee or
 * cancel. Its own path (not the RSVP one) so free and RSVP never drift; swaps `freeStats` in place, no loading state.
 */
export const fetchFreeEventStats = createAsyncThunk<TicketingRsvpStats, EventHashedID>('eventTicketing/fetchFreeStats', (eventHashedID: EventHashedID) => {
  return window.PMW.readLocal('eventticketing/getFreeEventStats', {event_hid: eventHashedID});
});

/**
 * Re-reads the row summaries for specific orders and merges them over the loaded ones. The door scanner admits
 * attendees without going through the table, so on closing it the dashboard refreshes just the orders it touched —
 * cheaper than a full reload, and it keeps the organiser's search and status filter, which a reload would clear.
 *
 * jQuery encodes the array as repeated `order_ids[]=…` params, which PHP reads back via `getGet('order_ids')`.
 */
export const fetchTicketingOrderSummaries = createAsyncThunk<TicketingOrderSummary[], FetchTicketingOrderSummariesArgs>(
  'eventTicketing/fetchOrderSummaries',
  ({eventHashedID, orderIds}: FetchTicketingOrderSummariesArgs) => {
    return window.PMW.readLocal('eventticketing/getTicketingOrderSummaries', {event_hid: eventHashedID, order_ids: orderIds});
  },
);

/**
 * One page of the orders table on its own — used after the initial load for search, status filtering, and
 * lazy-loading further pages. The reducer routes the page into the cached default list or the fresh filtered list.
 *
 * jQuery encodes the statuses array as repeated `statuses[]=…` params, which PHP reads back as an array via
 * `getGet('statuses')` — the shape `TicketingOrdersFilterOpts::fromRequestValues` expects.
 */
export const fetchTicketingOrders = createAsyncThunk<TicketingOrdersBatch, FetchTicketingOrdersArgs>(
  'eventTicketing/fetchOrders',
  ({eventHashedID, currentPageNumber, search, statuses}: FetchTicketingOrdersArgs) => {
    return window.PMW.readLocal('eventticketing/getTicketingOrders', {
      event_hid: eventHashedID,
      currentPageNumber,
      search: search === '' ? undefined : search,
      statuses,
    });
  },
  {
    // Two mounted lists (dashboard + search/check-in overlay) run the lazy-load effect in the same render commit;
    // the first dispatch flips the active list to LOADING, so the duplicate page request is cancelled here.
    condition: ({search, statuses}, {getState}) => {
      const {eventTicketing} = getState() as RootState;
      return getActiveOrdersList(eventTicketing, search, statuses).loadingState !== LoadingStates.LOADING;
    },
  }
);

/**
 * Resends the whole order's ticket email(s) (B9): one email to the buyer for a per-order event, or one per
 * attendee for a per-attendee event. No state change, so nothing to merge into the store.
 */
export const resendTicketingTicket = createAsyncThunk(
  'eventTicketing/resendTicket',
  async ({eventHashedID, orderId}: {eventHashedID: EventHashedID; orderId: number}): Promise<void> => {
    await window.PMW.writeLocal('eventticketing/resendTicketingTicket', {event_hid: eventHashedID, order_id: orderId});
  }
);

/** Checks one attendee in (B8). Returns the order's updated row summary; the reducer merges it in place. */
export const checkInTicketingAttendee = createAsyncThunk<TicketingOrderSummary, CheckInTicketingAttendeeArgs>(
  'eventTicketing/checkInAttendee',
  ({eventHashedID, attendeePublicId}: CheckInTicketingAttendeeArgs) => {
    return window.PMW.writeLocal('eventticketing/checkInTicketingAttendee', {event_hid: eventHashedID, attendee_public_id: attendeePublicId});
  }
);

/** Undoes one attendee's check-in (Hi.Events `check_out`). Returns the order's updated row summary; the reducer merges it in place. */
export const checkOutTicketingAttendee = createAsyncThunk<TicketingOrderSummary, CheckInTicketingAttendeeArgs>(
  'eventTicketing/checkOutAttendee',
  ({eventHashedID, attendeePublicId}: CheckInTicketingAttendeeArgs) => {
    return window.PMW.writeLocal('eventticketing/checkOutTicketingAttendee', {event_hid: eventHashedID, attendee_public_id: attendeePublicId});
  }
);

/** Checks in every still-registered attendee of an order in one round trip (the table/kebab "Check in"). Returns the updated row summary; the reducer merges it in place. */
export const checkInTicketingOrder = createAsyncThunk<TicketingOrderSummary, CheckInTicketingOrderArgs>(
  'eventTicketing/checkInOrder',
  ({eventHashedID, orderId}: CheckInTicketingOrderArgs) => {
    return window.PMW.writeLocal('eventticketing/checkInTicketingOrder', {event_hid: eventHashedID, order_id: orderId});
  }
);

/**
 * Cancels `ticketQuantity` of an order's tickets WITHOUT a refund — the "Cancel tickets" action that replaces
 * Refund inside 24h of the event. Returns the order's updated row summary; the reducer merges it in place.
 */
export const cancelTicketingAttendees = createAsyncThunk<TicketingOrderSummary, CancelTicketingAttendeesArgs>(
  'eventTicketing/cancelAttendees',
  ({eventHashedID, orderId, ticketQuantity}: CancelTicketingAttendeesArgs) => {
    return window.PMW.writeLocal('eventticketing/cancelTicketingAttendees', {event_hid: eventHashedID, order_id: orderId, ticketQuantity});
  }
);

/** Marks an unpaid (offline) order as paid (B7). Returns the order's updated row summary; the reducer merges it into the hashmap in place. */
export const markTicketOrderAsPaid = createAsyncThunk<TicketingOrderSummary, MarkTicketOrderAsPaidArgs>(
  'eventTicketing/markOrderAsPaid',
  ({eventHashedID, orderId}: MarkTicketOrderAsPaidArgs) => {
    return window.PMW.writeLocal('eventticketing/markTicketOrderAsPaid', {event_hid: eventHashedID, order_id: orderId});
  }
);

/**
 * Adds an attendee by hand (B4) and returns the new order's row summary. Attendees go in the buyer's shape so the
 * backend reuses its TicketAttendee. The .fulfilled reducer inserts the new order at the top of the cached list.
 */
export const addTicketingAttendee = createAsyncThunk<TicketingOrderSummary, AddTicketingAttendeeArgs>(
  'eventTicketing/addAttendee',
  ({eventHashedID, quantity, attendees, paidInCash, checkInOnAdd, sendConfirmationEmail}: AddTicketingAttendeeArgs) => {
    const payload: Record<string, unknown> = {
      event_hid: eventHashedID,
      quantity,
      attendees: attendees.map((attendee) => ({
        name: attendee.name,
        email: attendee.email,
        question_answers: attendee.answers.map((answer) => ({question_id: answer.questionId, response: {answer: answer.answer}})),
      })),
    };
    if (paidInCash) {
      payload.paidInCash = '1';
    }
    if (checkInOnAdd) {
      payload.checkInOnAdd = '1';
    }
    if (sendConfirmationEmail) {
      payload.sendConfirmationEmail = '1';
    }
    return window.PMW.writeLocal('eventticketing/addTicketingAttendee', payload);
  }
);

/**
 * Refunds `ticketQuantity` of an order's tickets (B5). The backend decides whole-vs-partial from the quantity
 * against the order's active tickets (and cancels the refunded attendees), so the client just sends the count.
 * Returns the updated row summary plus the new refundable amount; the reducer merges the summary in place.
 */
export const refundTicketOrder = createAsyncThunk<TicketingRefundResult, RefundTicketOrderArgs>(
  'eventTicketing/refundOrder',
  ({eventHashedID, orderId, reason, refundCustomReason, ticketQuantity}: RefundTicketOrderArgs) => {
    return window.PMW.writeLocal('eventticketing/refundTicketOrder', {
      event_hid: eventHashedID,
      order_id: orderId,
      reason,
      refundCustomReason: refundCustomReason === '' ? undefined : refundCustomReason,
      ticketQuantity,
    });
  }
);
